import moment from 'moment';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Select } from 'components/FormFields';
import Tabs from 'components/Tabs';
import Toaster from 'components/Toaster';
import { useGetAccount } from 'pages/app/Accounts/gql/queries/useGetAccount';
import AccountTab from './AccountTab';
import DatesTimesTab from './DatesTimesTab';
import DetailsTab from './DetailsTab';
import FanInfoTab from './FanInfoTab';
import { useGetEventForEdit } from './gql/useGetEventForEdit';
import { useUpdateEventV3 } from './gql/useUpdateEventV3';
import LapSponsorshipTab from './LapSponsorshipTab';
import MediaMarketingTab from './MediaMarketingTab';
import NotesTab from './NotesTab';
import POSTab from './POSTab';
import PromoterTab from './PromoterTab';
import TicketsTab from './TicketsTab';
import WaiversTab from './WaiversTab';

// Page wrapper - ensures full viewport coverage with theme background
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

// Page container
const Container = styled.div`
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

// Page header
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

// Date display card - matches design system
const DateDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fa4616;
  color: white;
  border-radius: 8px;
  padding: 10px 16px;
  min-width: 70px;
  gap: 4px;
`;

const DatePill = styled.div`
  background: white;
  color: #1e293b;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  letter-spacing: 0.5px;
`;

const DateText = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 4px 0;
  line-height: 1;
`;

const TrackName = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`;

// Event Link row styles
const EventLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const EventLinkText = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #fa4616;
  font-weight: 500;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const EventLinkButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  transition: all 0.2s;

  &:hover {
    background: rgba(250, 70, 22, 0.15);
    color: #fa4616;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Button wrapper to control width for header buttons
const HeaderButton = styled.div`
  width: auto;

  button {
    width: auto;
    padding: 10px 24px;
  }
`;

// Event Status Options
const EVENT_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Status selector - Desktop version (expandable pills)
const StatusSelectorDesktop = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 4px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${({ theme, $status }) =>
    theme?.colors?.eventStatus?.[$status]?.background ||
    'rgba(100, 116, 139, 0.12)'};

  &.expanded {
    background: ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9'};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const StatusPill = styled.button`
  padding: 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  white-space: nowrap;
  overflow: hidden;
  max-width: 0;
  opacity: 0;

  &.active {
    max-width: 120px;
    padding: 8px 14px;
    opacity: 1;
    color: ${({ theme, $status }) =>
      theme?.colors?.eventStatus?.[$status]?.text || '#64748b'};
  }

  .expanded & {
    max-width: 120px;
    padding: 8px 14px;
    opacity: 1;
    color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#64748b')};

    &.active {
      background: ${({ theme }) =>
        theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'white'};
      color: ${({ theme, $status }) =>
        theme?.colors?.eventStatus?.[$status]?.text || '#64748b'};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    &:hover:not(.active) {
      background: ${({ theme }) =>
        theme?.name === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.03)'};
      color: ${({ theme, $status }) =>
        theme?.colors?.eventStatus?.[$status]?.text || '#64748b'};
    }
  }
`;

const StatusChevron = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  color: ${({ theme, $status }) =>
    theme?.colors?.eventStatus?.[$status]?.text || '#94a3b8'};
  transition: all 0.3s ease;

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.3s ease;
  }

  .expanded & {
    display: none;
  }
`;

// Status selector - Mobile version (dropdown)
const StatusSelectorMobile = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const StatusSelectWrapper = styled.div`
  position: relative;
  min-width: 130px;
`;

const StatusSelect = styled.select`
  width: 100%;
  appearance: none;
  padding: 8px 32px 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid
    ${({ theme, $status }) =>
      theme?.colors?.eventStatus?.[$status]?.border ||
      'rgba(100, 116, 139, 0.3)'};
  background: ${({ theme, $status }) =>
    theme?.colors?.eventStatus?.[$status]?.background ||
    'rgba(100, 116, 139, 0.12)'};
  color: ${({ theme, $status }) =>
    theme?.colors?.eventStatus?.[$status]?.text || '#64748b'};
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
  }

  option {
    background: ${({ theme }) =>
      theme?.name === 'dark' ? '#1e293b' : '#ffffff'};
    color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0f172a')};
    font-weight: 600;
    text-transform: none;
  }
`;

const StatusSelectChevron = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ theme, $status }) =>
    theme?.colors?.eventStatus?.[$status]?.text || '#64748b'};

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Desktop tabs wrapper - adds margin and hides on mobile
const DesktopTabsWrapper = styled.div`
  margin-bottom: 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile select container - shown only on mobile
const MobileTabSelect = styled.div`
  display: none;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    display: block;
  }
`;

// Placeholder for empty tabs
const PlaceholderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  font-size: 16px;
  gap: 16px;
`;

const PlaceholderIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;
`;

// Tab configuration for events
const getEventTabs = () => {
  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'tickets', label: 'Tickets' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'account', label: 'Account' },
    { id: 'media-marketing', label: 'Media & Marketing' },
    { id: 'fan-info', label: 'Fan Info' },
    { id: 'lap-sponsorship', label: 'Lap Sponsorship' },
  ];

  // Only show Waivers tab if not tickethoss platform
  if (process.env.REACT_APP_PLATFORM !== 'tickethoss') {
    tabs.push({ id: 'waivers', label: 'Waivers' });
  }

  tabs.push({ id: 'promoter', label: 'Promoter' });
  tabs.push({ id: 'notes', label: 'Notes' });
  tabs.push({ id: 'pos', label: 'POS' });

  return tabs;
};

// Icon components
const CalendarIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const LocationIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const GlobeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const CopyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Helper to get marketing website URL based on platform
const getMarketingUrl = (eventId) => {
  const platform = process.env.REACT_APP_PLATFORM;
  let domain = 'tickethoss.com';
  if (platform === 'pitpay') {
    domain = 'pitpay.com';
  } else if (platform === 'kartpass') {
    domain = 'kartpass.com';
  }
  return `https://${domain}/event/${eventId}`;
};

// Helper to parse date for display
const parseDateForDisplay = (startDateString, endDateString) => {
  if (!startDateString) return null;

  // Try to parse the start date - handle various formats
  const startDate = new Date(startDateString);
  if (isNaN(startDate.getTime())) return null;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Check if multi-day event
  let isMultiDay = false;
  let endDay = null;

  if (endDateString) {
    const endDate = new Date(endDateString);
    if (!isNaN(endDate.getTime())) {
      // Compare dates (ignoring time)
      const startDateOnly = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      );
      const endDateOnly = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate()
      );
      isMultiDay = endDateOnly > startDateOnly;
      if (isMultiDay) {
        endDay = endDate.getDate();
      }
    }
  }

  return {
    month: months[startDate.getMonth()],
    day: startDate.getDate(),
    dayOfWeek: days[startDate.getDay()],
    year: startDate.getFullYear(),
    isMultiDay,
    endDay,
  };
};

// Inner component that uses theme
const EditEventContent = () => {
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const eventId = params.id;

  // Context-aware routing
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Fetch event data
  const { event, loading, refetch } = useGetEventForEdit(eventId);

  // Mutation hook for saving
  const { updateEventV3 } = useUpdateEventV3();

  // Get tab from URL query params
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab');
  const TABS = getEventTabs();
  const validTabIds = TABS.map((t) => t.id);

  // Initialize activeTab from URL or default to 'details'
  const [activeTab, setActiveTab] = useState(() => {
    if (tabFromUrl && validTabIds.includes(tabFromUrl)) {
      return tabFromUrl;
    }
    return 'details';
  });

  // Sync activeTab with URL changes (e.g., when navigating back from add ticket)
  useEffect(() => {
    if (
      tabFromUrl &&
      validTabIds.includes(tabFromUrl) &&
      tabFromUrl !== activeTab
    ) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle tab change - update both state and URL
  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      // Update URL to reflect current tab (use replace to avoid cluttering history)
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('tab', tabId);
      history.replace({
        pathname: location.pathname,
        search: newSearchParams.toString(),
      });
    },
    [history, location.pathname, location.search]
  );

  const [saving, setSaving] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });
  const [linkCopied, setLinkCopied] = useState(false);

  // Marketing link URL
  const marketingUrl = eventId ? getMarketingUrl(eventId) : null;

  // Copy link to clipboard
  const handleCopyLink = useCallback(() => {
    if (marketingUrl) {
      navigator.clipboard.writeText(marketingUrl).then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      });
    }
  }, [marketingUrl]);

  // Open link in new tab
  const handleViewLink = useCallback(() => {
    if (marketingUrl) {
      window.open(marketingUrl, '_blank', 'noopener,noreferrer');
    }
  }, [marketingUrl]);

  // Lifted state for lap sponsorships - persists across tab switches
  const [lapSponsorships, setLapSponsorships] = useState([]);
  const [lapSponsorshipsInitialized, setLapSponsorshipsInitialized] =
    useState(false);

  // Lifted state for POS settings - persists across tab switches
  const [pos, setPos] = useState(false);
  const [posSettings, setPosSettings] = useState({});
  const [posInitialized, setPosInitialized] = useState(false);

  // Lifted state for Account settings - persists across tab switches
  const [accountId, setAccountId] = useState(null);
  const [accountFeeSettings, setAccountFeeSettings] = useState({});
  const [accountSettingsInitialized, setAccountSettingsInitialized] =
    useState(false);

  // Lifted state for Dates & Times - persists across tab switches
  const [dateSettings, setDateSettings] = useState({});
  const [adminTimes, setAdminTimes] = useState([]);
  const [datesTimesInitialized, setDatesTimesInitialized] = useState(false);

  // Lifted state for Details tab - persists across tab switches
  const [detailsSettings, setDetailsSettings] = useState({});
  const [detailsInitialized, setDetailsInitialized] = useState(false);

  // Lifted state for Media & Marketing tab - persists across tab switches
  const [mediaSettings, setMediaSettings] = useState({});
  const [mediaInitialized, setMediaInitialized] = useState(false);

  // Lifted state for Promoter tab - persists across tab switches
  const [promoterSettings, setPromoterSettings] = useState({});
  const [promoterInitialized, setPromoterInitialized] = useState(false);

  // Lifted state for Event Infos (Fan Info) - persists across tab switches
  const [eventInfos, setEventInfos] = useState({
    trackInfos: [],
    eventInfos: [],
    allInfos: [],
  });
  const [eventInfosInitialized, setEventInfosInitialized] = useState(false);

  // Lifted state for Event Notes - persists across tab switches
  const [eventNotes, setEventNotes] = useState([]);
  const [eventNotesInitialized, setEventNotesInitialized] = useState(false);

  // Lifted state for Waivers - persists across tab switches
  const [eventWaivers, setEventWaivers] = useState([]);
  const [waiversInitialized, setWaiversInitialized] = useState(false);

  // Status selector expansion state
  const [statusExpanded, setStatusExpanded] = useState(false);
  const [eventStatus, setEventStatus] = useState('draft');
  const statusSelectorRef = useRef(null);

  // Validation error state for tabs
  const [tabErrors, setTabErrors] = useState({});

  // Initialize status from event data
  const eventStatusFromServer = event?.status;
  useEffect(() => {
    if (eventStatusFromServer) {
      setEventStatus(eventStatusFromServer);
    }
  }, [eventStatusFromServer]);

  // Close status selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        statusSelectorRef.current &&
        !statusSelectorRef.current.contains(e.target)
      ) {
        setStatusExpanded(false);
      }
    };

    if (statusExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusExpanded]);

  // Fetch account data for divisions (needed by DetailsTab)
  const { data: accountData } = useGetAccount(accountId);

  // Initialize lap sponsorships from event data (only once when event loads)
  useEffect(() => {
    if (event && !lapSponsorshipsInitialized) {
      const sponsorshipsData = event.lap_sponsorships || [];
      const sorted = [...sponsorshipsData]
        .map((s, idx) => ({
          ...s,
          id: s.id || `lap-sponsorship-${Date.now()}-${idx}`,
          order: s.order ?? idx,
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setLapSponsorships(sorted);
      setLapSponsorshipsInitialized(true);
    }
  }, [event, lapSponsorshipsInitialized]);

  // Initialize POS settings from event data (only once when event loads)
  useEffect(() => {
    if (event && !posInitialized) {
      setPos(event.pos || false);
      setPosSettings(event.pos_settings || {});
      setPosInitialized(true);
    }
  }, [event, posInitialized]);

  // Initialize Account settings from event data (only once when event loads)
  useEffect(() => {
    if (event && !accountSettingsInitialized) {
      setAccountId(event.account_id || null);
      setAccountFeeSettings({
        marketing_fee: event.marketing_fee ?? false,
        international_fee: event.international_fee ?? false,
        hide_fee: event.hide_fee ?? false,
      });
      setAccountSettingsInitialized(true);
    }
  }, [event, accountSettingsInitialized]);

  // Initialize Dates & Times settings from event data (only once when event loads)
  useEffect(() => {
    if (event && !datesTimesInitialized) {
      setDateSettings({
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        sale_start: event.sale_start || '',
        sale_end: event.sale_end || '',
        on_sale_time: event.on_sale_time || '',
        off_sale_time: event.off_sale_time || '',
      });
      setAdminTimes(event.admin_times || []);
      setDatesTimesInitialized(true);
    }
  }, [event, datesTimesInitialized]);

  // Initialize Details settings from event data (only once when event loads)
  useEffect(() => {
    if (event && !detailsInitialized) {
      setDetailsSettings({
        name: event.name || '',
        about: event.about || '',
        event_types: event.event_types || [],
        capacity: event.capacity || '',
        track_id: event.track_id || null,
        reserved_seats: event.reserved_seats ?? false,
        sales_windows: event.sales_windows ?? false,
        admin_scan_out: event.admin_scan_out ?? false,
        admin_website_url: event.admin_website_url || '',
        admin_schedule_url: event.admin_schedule_url || '',
        code: event.code || '',
        code_date: event.code_date || '',
        code_time: event.code_time || '',
        division_ids: event.divisions?.map((d) => d.id) || [],
        series_ids: event.series_ids || [],
      });
      setDetailsInitialized(true);
    }
  }, [event, detailsInitialized]);

  // Initialize Media & Marketing settings from event data (only once when event loads)
  useEffect(() => {
    if (event && !mediaInitialized) {
      setMediaSettings({
        image: event.image || null,
        image_id: event.image_id || null,
        logo: event.logo || null,
        logo_id: event.logo_id || null,
        promo_image: event.promo_image || null,
        promo_image_id: event.promo_image_id || null,
        facebook_image: event.facebook_image || null,
        facebook_image_id: event.facebook_image_id || null,
        schedule_detail_image:
          event.schedule_detail_image || event.schedule_detail_pdf || null,
        schedule_detail_image_id: event.schedule_detail_image_id || null,
        armband_image: event.armband_image || null,
        armband_logo_id: event.armband_logo_id || null,
        facebook: event.facebook || '',
        instagram: event.instagram || '',
        twitter: event.twitter || '',
        url: event.url || '',
        info_video_url: event.info_video_url || '',
        schedule_details: event.schedule_details || '',
        schedule_detail_url: event.schedule_detail_url || '',
        faqs: event.faqs || [],
        show_pitpay: event.show_pitpay ?? false,
        show_tickethoss: event.show_tickethoss ?? false,
      });
      setMediaInitialized(true);
    }
  }, [event, mediaInitialized]);

  // Initialize Promoter settings from event data (only once when event loads)
  useEffect(() => {
    if (event && !promoterInitialized) {
      setPromoterSettings({
        promoter_name: event.promoter_name || '',
        promoter_organization: event.promoter_organization || '',
        promoter_email: event.promoter_email || '',
        promoter_phone: event.promoter_phone || '',
        promoter_website: event.promoter_website || '',
      });
      setPromoterInitialized(true);
    }
  }, [event, promoterInitialized]);

  // Initialize Event Infos from event data (only once when event loads)
  // This ensures fan info data is available even if the Fan Info tab is never visited
  useEffect(() => {
    if (event && !eventInfosInitialized) {
      // Get all available track infos
      const trackInfos = event.track?.infos || [];
      // Use info.id (category ID) for matching, not the junction record ID
      const trackInfoCategoryIds = new Set(
        trackInfos.map((t) => t.info?.id).filter(Boolean)
      );

      // Get all available series infos (from all series)
      const seriesList = event.series || [];
      const seriesInfosFlat = seriesList.flatMap((s) =>
        (s.infos || []).map((info) => ({
          ...info,
          seriesId: s.id,
          seriesName: s.name,
        }))
      );
      // Use info.id (category ID) for matching
      const seriesInfoCategoryIds = new Set(
        seriesInfosFlat.map((s) => s.info?.id).filter(Boolean)
      );

      // Get enabled infos from admin_infos (direct DB read, not cached)
      const enabledInfos = event.admin_infos || [];

      // Build the allInfos array - combining track and series infos that are enabled
      // Match by category ID (info.id) not junction record ID

      // Track infos that are enabled (match by category ID)
      const enabledTrackInfos = enabledInfos.filter(
        (i) => i.info?.id && trackInfoCategoryIds.has(i.info.id)
      );
      const trackInfosWithMeta = enabledTrackInfos.map((info) => ({
        ...info,
        enabled: true,
        source: 'track',
        trackInfo: trackInfos.find((t) => t.info?.id === info.info?.id) || info,
      }));

      // Series infos that are enabled (match by category ID, exclude those already matched as track)
      const enabledSeriesInfos = enabledInfos.filter(
        (i) =>
          i.info?.id &&
          seriesInfoCategoryIds.has(i.info.id) &&
          !trackInfoCategoryIds.has(i.info.id)
      );
      const seriesInfosWithMeta = enabledSeriesInfos.map((info) => ({
        ...info,
        enabled: true,
        source: 'series',
        seriesInfo:
          seriesInfosFlat.find((s) => s.info?.id === info.info?.id) || info,
        seriesName: seriesInfosFlat.find((s) => s.info?.id === info.info?.id)
          ?.seriesName,
      }));

      // Event-specific infos (those not matching any track or series category)
      const eventSpecificInfos = enabledInfos
        .filter(
          (i) =>
            !i.info?.id ||
            (!trackInfoCategoryIds.has(i.info.id) &&
              !seriesInfoCategoryIds.has(i.info.id))
        )
        .map((info) => ({
          ...info,
          enabled: true,
          source: 'event',
          isEventInfo: true,
        }));

      // Combine all infos and sort by order
      const allInfos = [
        ...trackInfosWithMeta,
        ...seriesInfosWithMeta,
        ...eventSpecificInfos,
      ].sort((a, b) => (a.order || 0) - (b.order || 0));

      setEventInfos({
        trackInfos: trackInfosWithMeta,
        seriesInfos: seriesInfosWithMeta,
        eventInfos: eventSpecificInfos,
        allInfos,
      });
      setEventInfosInitialized(true);
    }
  }, [event, eventInfosInitialized]);

  // Initialize Event Notes from event data (only once when event loads)
  // This ensures notes data is available even if the Notes tab is never visited
  useEffect(() => {
    if (event && !eventNotesInitialized) {
      // Event-specific notes (not account notes which are read-only)
      const notes = event.notes || [];
      setEventNotes(notes);
      setEventNotesInitialized(true);
    }
  }, [event, eventNotesInitialized]);

  // Initialize Waivers from event data (only once when event loads)
  // This ensures waivers data is available even if the Waivers tab is never visited
  useEffect(() => {
    if (event && !waiversInitialized) {
      const waivers = event.waivers || [];
      setEventWaivers(waivers);
      setWaiversInitialized(true);
    }
  }, [event, waiversInitialized]);

  // Handler for lap sponsorship changes
  const handleLapSponsorshipsChange = useCallback((updatedSponsorships) => {
    setLapSponsorships(updatedSponsorships);
    setLapSponsorshipsInitialized(true);
  }, []);

  // Handlers for POS changes
  const handlePosChange = useCallback((value) => {
    setPos(value);
    // Clear POS tab error when user toggles POS
    setTabErrors((prev) => ({ ...prev, pos: null }));
  }, []);

  const handlePosSettingsChange = useCallback((updatedSettings) => {
    setPosSettings(updatedSettings);
    // Clear POS tab error when user changes settings
    setTabErrors((prev) => ({ ...prev, pos: null }));
  }, []);

  // Handlers for Account settings changes
  const handleAccountIdChange = useCallback((newAccountId) => {
    setAccountId(newAccountId);
  }, []);

  const handleAccountFeeSettingsChange = useCallback((updatedSettings) => {
    setAccountFeeSettings(updatedSettings);
    // Clear account tab error when user changes fee settings
    setTabErrors((prev) => ({ ...prev, account: null }));
  }, []);

  // Handlers for Dates & Times changes
  const handleDateSettingsChange = useCallback(
    (updatedSettings) => {
      // Check if event dates changed and update admin times accordingly
      const oldStart = dateSettings.start_date;
      const oldEnd = dateSettings.end_date;
      const newStart = updatedSettings.start_date;
      const newEnd = updatedSettings.end_date;

      // Track if we need to also update sale_end in the settings
      let settingsToSave = updatedSettings;

      if (
        oldStart &&
        oldEnd &&
        newStart &&
        newEnd &&
        (oldStart !== newStart || oldEnd !== newEnd)
      ) {
        const oldStartM = moment(oldStart, ['MM-DD-YYYY', 'YYYY-MM-DD']);
        const newStartM = moment(newStart, ['MM-DD-YYYY', 'YYYY-MM-DD']);
        const oldEndM = moment(oldEnd, ['MM-DD-YYYY', 'YYYY-MM-DD']);
        const newEndM = moment(newEnd, ['MM-DD-YYYY', 'YYYY-MM-DD']);

        if (
          oldStartM.isValid() &&
          newStartM.isValid() &&
          oldEndM.isValid() &&
          newEndM.isValid()
        ) {
          const oldDayCount = oldEndM.diff(oldStartM, 'days') + 1;
          const newDayCount = newEndM.diff(newStartM, 'days') + 1;
          const offsetDays = newStartM.diff(oldStartM, 'days');

          // Update admin times if they exist
          if (adminTimes.length > 0) {
            if (oldDayCount === newDayCount && offsetDays !== 0) {
              // Same number of days - shift each admin time date by offset
              const updatedTimes = adminTimes.map((time) => {
                const timeDate = moment(time.event_date, [
                  'MM-DD-YYYY',
                  'YYYY-MM-DD',
                ]);
                if (timeDate.isValid()) {
                  return {
                    ...time,
                    event_date: timeDate
                      .add(offsetDays, 'days')
                      .format('MM-DD-YYYY'),
                  };
                }
                return time;
              });
              setAdminTimes(updatedTimes);
            } else if (oldDayCount !== newDayCount) {
              // Different number of days - regenerate from new date range, preserving times
              const newTimes = [];
              const current = newStartM.clone();
              let dayIndex = 0;
              while (current.isSameOrBefore(newEndM, 'day')) {
                const existingTime = adminTimes[dayIndex] || {};
                newTimes.push({
                  event_date: current.format('MM-DD-YYYY'),
                  gate_time: existingTime.gate_time || '',
                  start_time: existingTime.start_time || '',
                });
                current.add(1, 'day');
                dayIndex++;
              }
              setAdminTimes(newTimes);
            }
          }

          // Auto-update sale_end if it matched the old end_date
          if (!oldEndM.isSame(newEndM, 'day') && updatedSettings.sale_end) {
            const saleEndM = moment(updatedSettings.sale_end, [
              'MM-DD-YYYY',
              'YYYY-MM-DD',
            ]);
            if (saleEndM.isValid() && saleEndM.isSame(oldEndM, 'day')) {
              settingsToSave = {
                ...updatedSettings,
                sale_end: newEndM.format('MM-DD-YYYY'),
              };
            }
          }
        }
      }

      setDateSettings(settingsToSave);
    },
    [dateSettings, adminTimes]
  );

  const handleAdminTimesChange = useCallback((updatedTimes) => {
    setAdminTimes(updatedTimes);
  }, []);

  // Handler for Details settings changes
  const handleDetailsSettingsChange = useCallback((updatedSettings) => {
    setDetailsSettings(updatedSettings);
  }, []);

  // Handler for Media & Marketing settings changes
  const handleMediaSettingsChange = useCallback((updatedSettings) => {
    setMediaSettings(updatedSettings);
  }, []);

  // Handler for Promoter settings changes
  const handlePromoterSettingsChange = useCallback((updatedSettings) => {
    setPromoterSettings(updatedSettings);
  }, []);

  // Handler for Event Infos changes
  const handleEventInfosChange = useCallback((infosData) => {
    setEventInfos(infosData);
    setEventInfosInitialized(true);
  }, []);

  // Handler for Event Notes changes
  const handleEventNotesChange = useCallback((updatedNotes) => {
    setEventNotes(updatedNotes);
    setEventNotesInitialized(true);
  }, []);

  // Handler for Waivers changes
  const handleWaiversChange = useCallback((updatedWaivers) => {
    setEventWaivers(updatedWaivers);
    setWaiversInitialized(true);
  }, []);

  const getTabPlaceholder = (tabId) => {
    const placeholders = {
      details: 'Event details and information',
      tickets: 'Ticket configuration',
      schedule: 'Event schedule and dates configuration',
      account: 'Account association settings',
      pos: 'Point of sale settings',
      'lap-sponsorship': 'Lap sponsorship settings',
      notes: 'Event notes and comments',
      waivers: 'Waiver requirements',
      'fan-info': 'Fan information and preferences',
    };
    return placeholders[tabId] || 'Content coming soon';
  };

  const handleCancel = () => {
    history.push(`${routePrefix}/events/menu/${eventId}`);
  };

  // Recursively strip __typename from objects/arrays before sending to mutation
  const stripTypename = (value) => {
    if (Array.isArray(value)) {
      return value.map(stripTypename);
    }
    if (value !== null && typeof value === 'object') {
      const cleaned = {};
      Object.keys(value).forEach((key) => {
        if (key !== '__typename') {
          cleaned[key] = stripTypename(value[key]);
        }
      });
      return cleaned;
    }
    return value;
  };

  // Normalize a date string to MM-DD-YYYY format for the backend
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    // Already in MM-DD-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
    // Convert from YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${m}-${d}-${y}`;
    }
    return dateStr;
  };

  const handleSave = async (statusOverride) => {
    // Clear previous validation errors
    setTabErrors({});

    // Validate fee settings: either marketing_fee OR hide_fee must be enabled
    const hasMarketingFee = accountFeeSettings.marketing_fee === true;
    const hasPromoterAbsorbsFees = accountFeeSettings.hide_fee === true;

    if (!hasMarketingFee && !hasPromoterAbsorbsFees) {
      const errorMessage =
        'Please enable either "Collect Marketing Fees" or "Promoter Absorbs Fees".';
      setTabErrors({ account: errorMessage });
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Validation Error',
        message:
          'Fee settings are required. Please enable either "Collect Marketing Fees" or "Promoter Absorbs Fees" in the Account tab.',
      });
      return;
    }

    // Validate POS settings: if POS is enabled, all settings must be configured
    if (pos) {
      const missingSettings = [];
      if (!posSettings?.payment) missingSettings.push('Payment Methods');
      if (
        posSettings?.calculator === undefined ||
        posSettings?.calculator === null
      )
        missingSettings.push('Cash Change Calculator');
      if (!posSettings?.ticket_delivery)
        missingSettings.push('Ticket Delivery');
      if (!posSettings?.receipt_delivery)
        missingSettings.push('Receipt Delivery');

      if (missingSettings.length > 0) {
        const errorMessage = `Please select an option for: ${missingSettings.join(
          ', '
        )}.`;
        setTabErrors({ pos: errorMessage });
        setToaster({
          isOpen: true,
          type: 'error',
          title: 'Validation Error',
          message: `POS is enabled but some settings are missing. Please configure: ${missingSettings.join(
            ', '
          )} in the POS tab.`,
        });
        return;
      }
    }

    setSaving(true);

    const input = {
      id: parseInt(eventId, 10),

      // Status
      status: typeof statusOverride === 'string' ? statusOverride : eventStatus,

      // Details
      name: detailsSettings.name,
      about: detailsSettings.about,
      reserved_seats: detailsSettings.reserved_seats,
      sales_windows: detailsSettings.sales_windows,
      event_types: detailsSettings.event_types,
      capacity: detailsSettings.capacity
        ? parseInt(detailsSettings.capacity, 10)
        : null,
      track_id: detailsSettings.track_id
        ? parseInt(detailsSettings.track_id, 10)
        : null,
      admin_scan_out: detailsSettings.admin_scan_out,
      admin_website_url: detailsSettings.admin_website_url,
      admin_schedule_url: detailsSettings.admin_schedule_url,
      code: detailsSettings.code,
      code_date: normalizeDate(detailsSettings.code_date),
      code_time: detailsSettings.code_time,
      division_ids: (detailsSettings.division_ids || []).map((id) =>
        parseInt(id, 10)
      ),
      series_ids: (detailsSettings.series_ids || []).map((id) =>
        parseInt(id, 10)
      ),

      // Dates & Times (all dates normalized to MM-DD-YYYY)
      start_date: normalizeDate(dateSettings.start_date),
      end_date: normalizeDate(dateSettings.end_date),
      sale_start: normalizeDate(dateSettings.sale_start),
      sale_end: normalizeDate(dateSettings.sale_end),
      on_sale_time: dateSettings.on_sale_time || null,
      off_sale_time: dateSettings.off_sale_time || null,
      admin_times: adminTimes
        .filter((time) => time.gate_time || time.start_time)
        .map((time) => ({
          event_date: normalizeDate(time.event_date),
          gate_time: time.gate_time || null,
          start_time: time.start_time || null,
        })),

      // Account
      account_id: accountId ? parseInt(accountId, 10) : null,
      marketing_fee: accountFeeSettings.marketing_fee,
      international_fee: accountFeeSettings.international_fee,
      hide_fee: accountFeeSettings.hide_fee,

      // POS
      pos: pos,
      pos_settings: stripTypename(posSettings),

      // Lap Sponsorships
      lap_sponsorships: lapSponsorships.map((s, index) => ({
        id:
          typeof s.id === 'string' && s.id.startsWith('lap-sponsorship-')
            ? null
            : s.id
            ? parseInt(s.id, 10)
            : null,
        name: s.name,
        num_laps: s.num_laps ? parseInt(s.num_laps, 10) : null,
        price: s.price != null ? parseFloat(s.price) : null,
        order: index,
        payout_1: s.payout_1 != null ? parseFloat(s.payout_1) : null,
        payout_1_label: s.payout_1_label || null,
        payout_2: s.payout_2 != null ? parseFloat(s.payout_2) : null,
        payout_2_label: s.payout_2_label || null,
        payout_3: s.payout_3 != null ? parseFloat(s.payout_3) : null,
        payout_3_label: s.payout_3_label || null,
      })),

      // Media & Marketing
      image_id: mediaSettings.image_id
        ? parseInt(mediaSettings.image_id, 10)
        : null,
      logo_id: mediaSettings.logo_id
        ? parseInt(mediaSettings.logo_id, 10)
        : null,
      promo_image_id: mediaSettings.promo_image_id
        ? parseInt(mediaSettings.promo_image_id, 10)
        : null,
      facebook_image_id: mediaSettings.facebook_image_id
        ? parseInt(mediaSettings.facebook_image_id, 10)
        : null,
      schedule_detail_image_id: mediaSettings.schedule_detail_image_id
        ? parseInt(mediaSettings.schedule_detail_image_id, 10)
        : null,
      armband_logo_id: mediaSettings.armband_logo_id
        ? parseInt(mediaSettings.armband_logo_id, 10)
        : null,
      facebook: mediaSettings.facebook || null,
      instagram: mediaSettings.instagram || null,
      twitter: mediaSettings.twitter || null,
      url: mediaSettings.url || null,
      info_video_url: mediaSettings.info_video_url || null,
      schedule_details: mediaSettings.schedule_details || null,
      schedule_detail_url: mediaSettings.schedule_detail_url || null,
      faqs: (mediaSettings.faqs || []).map((faq) => ({
        question: faq.question,
        answer: faq.answer,
      })),
      show_pitpay: mediaSettings.show_pitpay,
      show_tickethoss: mediaSettings.show_tickethoss,

      // Promoter
      promoter_name: promoterSettings.promoter_name || null,
      promoter_organization: promoterSettings.promoter_organization || null,
      promoter_email: promoterSettings.promoter_email || null,
      promoter_phone: promoterSettings.promoter_phone || null,
      promoter_website: promoterSettings.promoter_website || null,

      // Note: Tickets are now saved immediately via createEventTicket/updateEventTicket mutations
      // so we no longer include them in the event save

      // Fan Info (unified admin_infos array - backend calculates exclusions)
      admin_infos: (eventInfos.allInfos || []).map((info, index) => {
        const baseInfo = {
          id: info.id ? parseInt(info.id, 10) : null,
          type: info.source,
          order: index,
        };

        if (info.source === 'event' || info.isEventInfo) {
          return {
            ...baseInfo,
            id:
              typeof info.id === 'string' && info.id.startsWith('event-info-')
                ? null
                : info.id
                ? parseInt(info.id, 10)
                : null,
            info_id: parseInt(info.info?.value || info.info?.id, 10) || null,
            name: info.name,
            text: info.text || null,
            link: info.link || null,
            video_url: info.video_url || null,
            logo_id: info.logo_id ? parseInt(info.logo_id, 10) : null,
            image_id: info.image_id ? parseInt(info.image_id, 10) : null,
            pdf_id: info.pdf_id ? parseInt(info.pdf_id, 10) : null,
          };
        }

        return baseInfo;
      }),

      // Notes (event-specific only - account notes are read-only)
      event_notes: eventNotes.map((note) => ({
        id:
          typeof note.id === 'string' && note.id.startsWith('event-note-')
            ? null
            : note.id
            ? parseInt(note.id, 10)
            : null,
        title: note.title,
        note: note.note,
        type: note.type,
        image_id: note.image_id ? parseInt(note.image_id, 10) : null,
      })),

      // Waivers (send as array of waiver IDs)
      waivers: eventWaivers.filter((w) => w.id).map((w) => parseInt(w.id, 10)),
    };

    try {
      await updateEventV3({ variables: { input } });

      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Success!',
        message: 'Event saved successfully.',
      });

      // Refetch to get fresh data from server
      refetch();
    } catch (err) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: err.message || 'Failed to save event. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    // Handle details tab
    if (activeTab === 'details') {
      return (
        <DetailsTab
          settings={detailsSettings}
          onSettingsChange={handleDetailsSettingsChange}
          divisions={accountData?.getAccount?.divisions || []}
          loading={loading}
        />
      );
    }

    // Handle media-marketing tab
    if (activeTab === 'media-marketing') {
      return (
        <MediaMarketingTab
          settings={mediaSettings}
          onSettingsChange={handleMediaSettingsChange}
          loading={loading}
        />
      );
    }

    // Handle promoter tab
    if (activeTab === 'promoter') {
      return (
        <PromoterTab
          settings={promoterSettings}
          onSettingsChange={handlePromoterSettingsChange}
          loading={loading}
        />
      );
    }

    // Handle tickets tab with actual component
    if (activeTab === 'tickets') {
      return (
        <TicketsTab
          event={event}
          loading={loading}
          routePrefix={routePrefix}
          onRefetch={refetch}
        />
      );
    }

    // Handle fan-info tab
    if (activeTab === 'fan-info') {
      return (
        <FanInfoTab
          event={event}
          loading={loading}
          routePrefix={routePrefix}
          onRefetch={refetch}
          eventInfos={eventInfos}
          onEventInfosChange={handleEventInfosChange}
        />
      );
    }

    // Handle notes tab
    if (activeTab === 'notes') {
      return (
        <NotesTab
          event={event}
          loading={loading}
          eventNotes={eventNotes}
          onEventNotesChange={handleEventNotesChange}
        />
      );
    }

    // Handle lap-sponsorship tab
    if (activeTab === 'lap-sponsorship') {
      return (
        <LapSponsorshipTab
          event={event}
          loading={loading}
          sponsorships={lapSponsorships}
          onSponsorshipsChange={handleLapSponsorshipsChange}
        />
      );
    }

    // Handle POS tab
    if (activeTab === 'pos') {
      return (
        <POSTab
          pos={pos}
          posSettings={posSettings}
          onPosChange={handlePosChange}
          onPosSettingsChange={handlePosSettingsChange}
          loading={loading}
          posSettingsError={tabErrors.pos}
        />
      );
    }

    // Handle Account tab
    if (activeTab === 'account') {
      return (
        <AccountTab
          accountId={accountId}
          onAccountIdChange={handleAccountIdChange}
          feeSettings={accountFeeSettings}
          onFeeSettingsChange={handleAccountFeeSettingsChange}
          loading={loading}
          feeSettingsError={tabErrors.account}
        />
      );
    }

    // Handle Schedule tab (formerly Dates & Times)
    if (activeTab === 'schedule') {
      return (
        <DatesTimesTab
          dateSettings={dateSettings}
          adminTimes={adminTimes}
          onDateSettingsChange={handleDateSettingsChange}
          onAdminTimesChange={handleAdminTimesChange}
          scheduleSettings={{
            schedule_details: mediaSettings.schedule_details,
            schedule_detail_url: mediaSettings.schedule_detail_url,
            schedule_detail_image: mediaSettings.schedule_detail_image,
            schedule_detail_image_id: mediaSettings.schedule_detail_image_id,
          }}
          onScheduleSettingsChange={(updatedSchedule) => {
            handleMediaSettingsChange({
              ...mediaSettings,
              ...updatedSchedule,
            });
          }}
          loading={loading}
        />
      );
    }

    // Handle Waivers tab (only shown for non-tickethoss platforms)
    if (activeTab === 'waivers') {
      return (
        <WaiversTab
          waivers={eventWaivers}
          onWaiversChange={handleWaiversChange}
          accountWaivers={accountData?.getAccount?.waivers || []}
          loading={loading}
        />
      );
    }

    // All other tabs show placeholder for now
    return (
      <Card>
        <PlaceholderContent>
          <PlaceholderIcon>
            <CalendarIcon />
          </PlaceholderIcon>
          <span>{getTabPlaceholder(activeTab)}</span>
        </PlaceholderContent>
      </Card>
    );
  };

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <HeaderLeft>
            {(() => {
              const dateInfo = parseDateForDisplay(
                event?.start_date,
                event?.end_date
              );
              if (!dateInfo) return null;
              return (
                <DateDisplay>
                  {dateInfo.isMultiDay ? (
                    <>
                      <DatePill>{dateInfo.month}</DatePill>
                      <DateText>
                        {dateInfo.day} - {dateInfo.endDay}
                      </DateText>
                    </>
                  ) : (
                    <>
                      <DatePill>{dateInfo.dayOfWeek}</DatePill>
                      <DateText>
                        {dateInfo.month} {dateInfo.day}
                      </DateText>
                    </>
                  )}
                </DateDisplay>
              );
            })()}
            <EventInfo>
              <Title>
                {event?.name || (eventId ? 'Edit Event' : 'Add Event')}
              </Title>
              {event?.track?.name && (
                <TrackName>
                  <LocationIcon />
                  {event.track.name}
                </TrackName>
              )}
              {eventId && (
                <EventLinkRow>
                  <EventLinkText>
                    <GlobeIcon />
                    Event Link
                  </EventLinkText>
                  <EventLinkButton
                    type="button"
                    onClick={handleCopyLink}
                    title={linkCopied ? 'Copied!' : 'Copy URL'}
                  >
                    {linkCopied ? <CheckIcon /> : <CopyIcon />}
                  </EventLinkButton>
                  <EventLinkButton
                    type="button"
                    onClick={handleViewLink}
                    title="Open in new tab"
                  >
                    <ExternalLinkIcon />
                  </EventLinkButton>
                </EventLinkRow>
              )}
            </EventInfo>
          </HeaderLeft>
          <HeaderRight>
            {/* Desktop Status Selector - Expandable Pills */}
            <StatusSelectorDesktop
              ref={statusSelectorRef}
              $status={eventStatus}
              className={statusExpanded ? 'expanded' : ''}
              onClick={() => setStatusExpanded(!statusExpanded)}
            >
              {EVENT_STATUS_OPTIONS.map((option) => (
                <StatusPill
                  key={option.value}
                  type="button"
                  $status={option.value}
                  className={eventStatus === option.value ? 'active' : ''}
                  onClick={(e) => {
                    if (statusExpanded) {
                      e.stopPropagation();
                      setEventStatus(option.value);
                      setStatusExpanded(false);
                      if (option.value !== eventStatus) {
                        handleSave(option.value);
                      }
                    }
                    // When collapsed, let the click bubble to parent to expand
                  }}
                >
                  {option.label}
                </StatusPill>
              ))}
              <StatusChevron $status={eventStatus}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </StatusChevron>
            </StatusSelectorDesktop>

            {/* Mobile Status Selector - Dropdown */}
            <StatusSelectorMobile>
              <StatusSelectWrapper>
                <StatusSelect
                  $status={eventStatus}
                  value={eventStatus}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setEventStatus(newStatus);
                    if (newStatus !== eventStatus) {
                      handleSave(newStatus);
                    }
                  }}
                >
                  {EVENT_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </StatusSelect>
                <StatusSelectChevron $status={eventStatus}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </StatusSelectChevron>
              </StatusSelectWrapper>
            </StatusSelectorMobile>

            <HeaderButton>
              <ButtonV2 type="button" onClick={handleCancel}>
                Cancel
              </ButtonV2>
            </HeaderButton>
            <HeaderButton>
              <ButtonV2
                elevated
                type="button"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? 'Saving...'
                  : eventId
                  ? 'Save Changes'
                  : 'Create Event'}
              </ButtonV2>
            </HeaderButton>
          </HeaderRight>
        </PageHeader>

        {/* Desktop tabs */}
        <DesktopTabsWrapper>
          <Tabs
            tabs={TABS.map((tab) => ({
              ...tab,
              error: tabErrors[tab.id] || false,
            }))}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </DesktopTabsWrapper>

        {/* Mobile select dropdown */}
        <MobileTabSelect>
          <Select
            value={activeTab}
            onChange={(e) => handleTabChange(e.target.value)}
            options={TABS.map((tab) => ({
              label: tabErrors[tab.id] ? `${tab.label} (!)` : tab.label,
              value: tab.id,
            }))}
          />
        </MobileTabSelect>

        {renderTabContent()}
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const EditEventV2 = () => {
  return (
    <EnhancedThemeProvider>
      <EditEventContent />
    </EnhancedThemeProvider>
  );
};

export default EditEventV2;
